import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import {
  Coins, Plus, AlertTriangle, ArrowUpRight, Gift, Clock, CheckCircle2,
} from "lucide-react";

/**
 * DiploksWalletSummaryCard
 * --------------------------------
 * Compact balance-and-CTA card surfaced on the org_admin / event_manager /
 * school_admin dashboard. NEVER renders for delegate, chair, parent, faculty,
 * sponsor, judge, partner, volunteer.
 *
 * Self-hides when:
 *   - the role is not wallet-eligible
 *   - the wallet endpoint returns 4xx (account not org-linked, etc.)
 */
const WALLET_ROLES = ["org_admin", "event_manager", "school_admin"];

export default function DiploksWalletSummaryCard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!user || !WALLET_ROLES.includes(user.role)) {
      setHidden(true);
      return;
    }
    api.get("/diploks/wallet")
      .then((r) => setData(r.data))
      .catch(() => setHidden(true));
  }, [user]);

  if (hidden || !user || !WALLET_ROLES.includes(user.role)) return null;
  if (!data) {
    return (
      <div
        data-testid="diploks-wallet-summary-card-loading"
        className="rounded-2xl border border-slate-200 bg-white p-5 animate-pulse"
      >
        <div className="h-3 w-24 bg-slate-200 rounded mb-3" />
        <div className="h-8 w-32 bg-slate-200 rounded" />
      </div>
    );
  }

  const balance = data.balance?.total_available || 0;
  const lowBalance = data.low_balance;
  const expiringSoon = data.balance?.total_expiring_soon || 0;
  const ribbon = (data.campaign_status || []).find(
    (c) => c.state === "eligible" || c.state === "already_applied" || c.state === "ended"
  );

  return (
    <div
      data-testid="diploks-wallet-summary-card"
      className="relative overflow-hidden rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#0A192F] via-[#0F2C50] to-[#0F4C81] text-white p-5"
    >
      <div className="absolute -right-12 -top-12 h-32 w-32 rounded-full bg-[#D4AF37]/10 blur-3xl pointer-events-none" />
      <div className="flex items-start justify-between relative">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold flex items-center gap-1.5">
            <Coins className="h-3 w-3" /> Diploks Wallet
          </p>
          <p
            className="mt-2 text-3xl font-bold tracking-tight"
            data-testid="diploks-wallet-summary-balance"
          >
            {balance.toLocaleString()}
          </p>
          <p className="text-[11px] text-slate-300 mt-0.5">Diploks available</p>
          {expiringSoon > 0 && (
            <p className="mt-2 inline-flex items-center gap-1 text-[10px] text-amber-300">
              <Clock className="h-3 w-3" /> {expiringSoon} expiring soon
            </p>
          )}
        </div>
        <Link
          to="/dashboard/diploks/wallet"
          data-testid="diploks-wallet-summary-open-wallet"
          className="text-[11px] text-[#D4AF37] hover:underline inline-flex items-center gap-1"
        >
          Open wallet <ArrowUpRight className="h-3 w-3" />
        </Link>
      </div>

      {lowBalance && (
        <div
          data-testid="diploks-wallet-summary-low-balance"
          className="mt-3 px-3 py-2 rounded-lg bg-amber-900/30 border border-amber-700/40 text-[11px] text-amber-200 flex items-start gap-2"
        >
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>Your balance is running low. Top up to keep premium features running.</span>
        </div>
      )}

      {ribbon && ribbon.state === "eligible" && ribbon.cap > 0 && (
        <div
          data-testid="diploks-wallet-summary-founding-eligible"
          className="mt-3 px-3 py-2 rounded-lg bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[11px] text-[#D4AF37] flex items-start gap-2"
        >
          <Gift className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>
            Founding Organiser bonus —{" "}
            <span className="font-semibold">
              {ribbon.seats_left?.toLocaleString()}/{ribbon.cap?.toLocaleString()}
            </span>{" "}
            seats left
          </span>
        </div>
      )}
      {ribbon && ribbon.state === "already_applied" && (
        <div
          data-testid="diploks-wallet-summary-founding-already"
          className="mt-3 px-3 py-2 rounded-lg bg-emerald-900/30 border border-emerald-700/40 text-[11px] text-emerald-200 flex items-start gap-2"
        >
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>Founding Organiser bonus already applied to your wallet.</span>
        </div>
      )}

      <Link
        to="/dashboard/diploks/topup"
        data-testid="diploks-wallet-summary-topup-cta"
        className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A192F] font-bold text-xs uppercase tracking-wider px-4 py-2.5 rounded-md hover:bg-[#B5952F] transition-colors"
      >
        <Plus className="h-3.5 w-3.5" /> Top up Diploks
      </Link>
    </div>
  );
}
